import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Modal, ScrollView } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { Ionicons } from '@expo/vector-icons';
import i18n from 'i18n-js';

const HelperModal = ({ visible, type, onClose, openedFromInfo = false }) => {
  const renderTimeoutContent = () => (
    <>
      <Ionicons name="time-outline" size={48} color="#4b4132" style={styles.icon} />
      <Text style={styles.title}>
        {i18n.t('camScanner.timeoutTitle', { defaultValue: 'Scanning Tips' })}
      </Text>
      
      <ScrollView style={styles.contentScroll}>
        <Text style={styles.message}>
          {i18n.t('camScanner.timeoutMessage', { 
            defaultValue: 'Having trouble scanning? Try these tips:' 
          })}
        </Text>

        <View style={styles.tipsList}>
          <View style={styles.tipItem}>
            <Ionicons name="sunny-outline" size={24} color="#4b4132" />
            <Text style={styles.tipText}>
              Avoid glare and shadows. Use <Ionicons name="flash" size={14} color="#4b4132" /> for dark environments.
            </Text>
          </View>

          <View style={styles.tipItem}>
            <Ionicons name="camera-reverse" size={24} color="#4b4132" />
            <Text style={styles.tipText}>
              Cam icon switches between cameras on the device.
            </Text>
          </View>

          <View style={styles.tipItem}>
            <Ionicons name="camera-reverse" size={24} color="#4b4132" />
            <Text style={styles.tipText}>
              Select a device camera that focuses on the barcode at close range.
            </Text>
          </View>

          <View style={styles.tipItem}>
            <Ionicons name="scan-outline" size={24} color="#4b4132" />
            <Text style={styles.tipText}>
              Bring the camera close to fill the guide with the barcode if needed.
            </Text>
          </View>

          <View style={styles.tipItem}>
            <Ionicons name="search-outline" size={24} color="#4b4132" />
            <Text style={styles.tipText}>
              Increasing zoom level may help but barcode must remain in focus.
            </Text>
          </View>

          <View style={styles.tipItem}>
            <Ionicons name="hand-left-outline" size={24} color="#4b4132" />
            <Text style={styles.tipText}>
              Minimize camera movement while scanning in low light.
            </Text>
          </View>

          <View style={styles.tipItem}>
            <Ionicons name="help-circle-outline" size={24} color="#4b4132" />
            <Text style={styles.tipText}>
              Still not scanning? Please check barcode is readable with other scanners.
            </Text>
          </View>
        </View>
      </ScrollView>
    </>
  );

  const render2DContent = () => (
    <>
      <Ionicons name="qr-code-outline" size={48} color="#4b4132" style={styles.icon} />
      <Text style={styles.title}>
        {i18n.t('camScanner.2dTitle', { defaultValue: '2D Barcode Detected' })}
      </Text>
      
      <ScrollView style={styles.contentScroll}>
        <Text style={styles.message}>
          {i18n.t('camScanner.2dMessage', { 
            defaultValue: 'This appears to be a 2D barcode (QR Code, Data Matrix, PDF417, or Aztec). These formats are not currently supported.' 
          })}
        </Text>

        <View style={styles.infoBox}>
          <Text style={styles.infoTitle}>
            {i18n.t('camScanner.supportedFormats', { defaultValue: 'Supported 1D Barcodes:' })}
          </Text>
          <Text style={styles.infoText}>
            • UPC-A, UPC-E{'\n'}
            • EAN-8, EAN-13{'\n'}
            • Code 39, Code 93, Code 128{'\n'}
            • ITF, Codabar
          </Text>
        </View>

        <Text style={styles.suggestion}>
          {i18n.t('camScanner.2dSuggestion', { 
            defaultValue: 'Please use a standard 1D barcode if available, or enter the code manually.' 
          })}
        </Text>
      </ScrollView>
    </>
  );

  return (
    <Modal
      visible={visible}
      transparent={true}
      animationType="fade"
      onRequestClose={onClose}
    >
      <View style={styles.overlay}>
        <LinearGradient
          colors={['#d47d02', '#ffb636']}
          start={{ x: 0, y: 1 }}
          end={{ x: 0, y: 0 }}
          style={styles.modalContainer}
        >
          <TouchableOpacity
            style={styles.cancelIcon}
            onPress={onClose}
            testID="closeHelperX"
          >
            <Ionicons name="close" size={24} color="#4b4132" />
          </TouchableOpacity>

          {type === 'timeout' ? renderTimeoutContent() : render2DContent()}

          {!openedFromInfo && (
            <TouchableOpacity
              style={styles.closeButton}
              onPress={onClose}
              testID="closeHelperButton"
            >
              <View style={styles.closeButtonContent}>
                <Text style={styles.closeButtonText}>
                  {i18n.t('camScanner.tapInfo', { defaultValue: 'Tap' })}
                </Text>
                <Ionicons name="information-circle" size={18} color="#fff" style={{ marginHorizontal: 4 }} />
                <Text style={styles.closeButtonText}>
                  {i18n.t('camScanner.toSeeTips', { defaultValue: 'to see these tips again' })}
                </Text>
              </View>
            </TouchableOpacity>
          )}
        </LinearGradient>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContainer: {
    width: '90%',
    maxWidth: 450,
    maxHeight: '80%',
    borderRadius: 10,
    padding: 20,
    alignItems: 'center',
    position: 'relative',
  },
  cancelIcon: {
    position: 'absolute',
    top: 10,
    right: 10,
    zIndex: 10,
    padding: 4,
  },
  icon: {
    marginBottom: 15,
  },
  title: {
    fontSize: 22,
    fontWeight: 'bold',
    color: '#4b4132',
    marginBottom: 15,
    textAlign: 'center',
  },
  contentScroll: {
    width: '100%',
    marginBottom: 20,
  },
  message: {
    fontSize: 16,
    color: '#4b4132',
    marginBottom: 20,
    textAlign: 'center',
    lineHeight: 22,
  },
  tipsList: {
    width: '100%',
  },
  tipItem: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.7)',
    padding: 12,
    borderRadius: 8,
    marginBottom: 10,
  },
  tipText: {
    flex: 1,
    fontSize: 14,
    color: '#4b4132',
    marginLeft: 12,
    lineHeight: 20,
  },
  infoBox: {
    backgroundColor: 'rgba(255, 255, 255, 0.7)',
    padding: 15,
    borderRadius: 8,
    marginBottom: 15,
  },
  infoTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#4b4132',
    marginBottom: 10,
  },
  infoText: {
    fontSize: 14,
    color: '#4b4132',
    lineHeight: 22,
  },
  suggestion: {
    fontSize: 14,
    color: '#4b4132',
    textAlign: 'center',
    fontStyle: 'italic',
    lineHeight: 20,
  },
  closeButton: {
    backgroundColor: '#4CAF50',
    paddingVertical: 12,
    paddingHorizontal: 30,
    borderRadius: 5,
    alignItems: 'center',
    width: '100%',
  },
  closeButtonContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  closeButtonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: 'bold',
  },
});

export default HelperModal;
